/**
 * APP PROMOTION — shared helpers for the /google-ads/app-promotion module.
 *
 * Google App campaigns have no WhisperCRM precedent (WhisperCRM only ever
 * built Meta's OUTCOME_APP_PROMOTION), so this module borrows the YouTube
 * workspace's structure — step rail, top strip, sticky preview — and swaps
 * the video preview for a mock store-listing ad card.
 */
import { Image as ImageIcon, Play, Smartphone, Star } from 'lucide-react';
import type { GoogleAdsAppBiddingGoal, GoogleAdsAppStore } from '@/services/google-ads.service';
import { parseYoutubeId } from '@/pages/google-ads/googleAdsTargeting';

export const APP_BASE = '/google-ads/app-promotion';

export const APP_STORES: Array<{ value: GoogleAdsAppStore; label: string; idLabel: string; idHint: string; example: string }> = [
  {
    value: 'GOOGLE_APP_STORE', label: 'Google Play',
    idLabel: 'Package name', idHint: 'The id= part of the Play Store URL', example: 'com.example.app',
  },
  {
    value: 'APPLE_APP_STORE', label: 'Apple App Store',
    idLabel: 'App Store id', idHint: 'The digits after "id" in the App Store URL', example: '284882215',
  },
];
export const storeLabel = (v?: string | null) => APP_STORES.find((s) => s.value === v)?.label ?? (v ? v.replace(/_/g, ' ') : '—');

export const APP_GOALS: Array<{ value: GoogleAdsAppBiddingGoal; label: string; short: string; hint: string; cpaLabel: string }> = [
  {
    value: 'OPTIMIZE_INSTALLS_TARGET_INSTALL_COST',
    label: 'Installs — target cost per install', short: 'App installs',
    hint: 'Get as many installs as possible at your target cost. The default for a new app.',
    cpaLabel: 'Target cost per install',
  },
  {
    value: 'OPTIMIZE_IN_APP_CONVERSIONS_TARGET_INSTALL_COST',
    label: 'Installs from users likely to act — target cost per install', short: 'Installs likely to act',
    hint: 'Still bids per install, but finds users likely to complete in-app actions. Needs in-app conversion tracking.',
    cpaLabel: 'Target cost per install',
  },
  {
    value: 'OPTIMIZE_IN_APP_CONVERSIONS_TARGET_CONVERSION_COST',
    label: 'In-app actions — target cost per action', short: 'In-app actions',
    hint: 'Optimises for in-app conversions (purchases, sign-ups) at a target cost per action. Needs in-app conversion tracking.',
    cpaLabel: 'Target cost per action',
  },
];
export const goalMeta = (v?: string | null) => APP_GOALS.find((g) => g.value === v);

/** Google's hard limits for AppAdInfo. */
export const APP_AD_LIMITS = {
  headlines: { min: 2, max: 5, chars: 30 },
  descriptions: { min: 1, max: 5, chars: 90 },
  images: { max: 20 },
  videos: { max: 20 },
} as const;

/**
 * Store URL / raw id → { store, appId }. Accepts:
 *   https://play.google.com/store/apps/details?id=com.x.y
 *   https://apps.apple.com/ae/app/name/id284882215
 *   com.x.y  ·  284882215  ·  id284882215
 */
export function parseStoreInput(raw: string): { store: GoogleAdsAppStore; appId: string } | null {
  const s = raw.trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    const host = u.hostname.toLowerCase();
    if (host.endsWith('play.google.com')) {
      const id = u.searchParams.get('id');
      return id ? { store: 'GOOGLE_APP_STORE', appId: id } : null;
    }
    if (host.endsWith('apps.apple.com') || host.endsWith('itunes.apple.com')) {
      const m = u.pathname.match(/\/id(\d{6,})/);
      return m ? { store: 'APPLE_APP_STORE', appId: m[1] } : null;
    }
    return null;
  } catch { /* not a URL */ }
  if (/^id\d{6,}$/i.test(s)) return { store: 'APPLE_APP_STORE', appId: s.slice(2) };
  if (/^\d{6,}$/.test(s)) return { store: 'APPLE_APP_STORE', appId: s };
  if (/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(s)) return { store: 'GOOGLE_APP_STORE', appId: s };
  return null;
}

export const isValidAppId = (store: GoogleAdsAppStore, appId: string) =>
  store === 'GOOGLE_APP_STORE'
    ? /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(appId.trim())
    : /^\d{6,}$/.test(appId.trim());

// ── preview ──────────────────────────────────────────────────────────────

export interface AppAdPreviewData {
  store: GoogleAdsAppStore;
  appId: string;
  /** Store-listing icon (from the AI inspector) — Google always shows the real icon, never an uploaded image. */
  iconUrl?: string | null;
  /** Store-listing name — shown under the headline like Play does. */
  appName?: string | null;
  headlines: string[];
  descriptions: string[];
  imageUrls: string[];
  videoUrls: string[];
}

/** Mock of how Google composes an App ad on Play / Search — indicative only. */
export function AppAdPreview({ ad }: { ad: AppAdPreviewData }) {
  const headline = ad.headlines.find((h) => h.trim()) || 'Your headline appears here';
  const description = ad.descriptions.find((d) => d.trim()) || 'Your description appears here — what the app does and why to install it.';
  const icon = ad.iconUrl || undefined;
  const media = ad.imageUrls.slice(0, 3);
  const video = ad.videoUrls.map(parseYoutubeId).find((v): v is string => !!v);
  const isPlay = ad.store === 'GOOGLE_APP_STORE';

  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="border-b border-border px-4 py-2.5 text-xs font-semibold text-text-muted">
        Preview · how it reads on {isPlay ? 'Google Play' : 'Google Search'}
      </div>
      <div className="p-4">
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-soft">
          {(video || media.length > 0) && (
            <div className="relative aspect-video w-full bg-surface-alt">
              {video ? (
                <>
                  <img src={`https://i.ytimg.com/vi/${video}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <Play size={30} className="fill-white text-white drop-shadow" />
                  </span>
                </>
              ) : (
                <div className={`grid h-full w-full gap-0.5 ${media.length === 1 ? 'grid-cols-1' : media.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {media.map((m) => <img key={m} src={m} alt="" className="h-full w-full object-cover" />)}
                </div>
              )}
              <span className="absolute bottom-2 left-2 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">Ad</span>
            </div>
          )}

          <div className="flex gap-3 p-3">
            {icon
              ? <img src={icon} alt="" referrerPolicy="no-referrer" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
              : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-surface-alt text-text-muted"><Smartphone size={20} /></span>}
            <div className="min-w-0 flex-1">
              <div className="line-clamp-2 text-[13px] font-medium leading-snug text-text-primary">{headline}</div>
              <div className="mt-0.5 flex items-center gap-1 text-2xs text-text-muted">
                <span className="truncate">{ad.appName || ad.appId || (isPlay ? 'com.example.app' : 'App Store id')}</span>
                <span>·</span><span>Sponsored</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-2xs text-text-muted">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={9} className="fill-warning text-warning" />)}
                <span className="ml-1">{isPlay ? 'Google Play' : 'App Store'}</span>
              </div>
            </div>
          </div>

          <div className="px-3 pb-3">
            <p className="line-clamp-3 text-2xs leading-relaxed text-text-muted">{description}</p>
          </div>

          <div className="border-t border-border p-3">
            <div className="rounded-lg bg-primary-soft px-3 py-2 text-center text-[12px] font-medium text-primary">
              {isPlay ? 'Install' : 'Get'}
            </div>
          </div>
        </div>

        {ad.imageUrls.length === 0 && ad.videoUrls.length === 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-surface-alt/60 px-3 py-2 text-2xs text-text-muted">
            <ImageIcon size={13} className="shrink-0" /> Add images or a YouTube video — Google serves text-only app ads to far fewer placements.
          </div>
        )}
        <p className="mt-3 text-[10px] leading-relaxed text-text-muted">
          Indicative only — Google mixes your headlines, descriptions, images and videos per placement
          (Play, Search, YouTube, Discover, Display) and pulls the icon, name and rating from the store listing.
        </p>
      </div>
    </div>
  );
}

// ── image aspect ratios ────────────────────────────────────────────────────
// Google accepts exactly three shapes for App campaign image assets; anything
// else is refused at ad create (ASPECT_RATIO_NOT_ALLOWED). Uploads that don't
// match are cropped to the nearest shape through ImageKit's URL transforms
// (`/tr:ar-…,w-…,fo-auto/` = smart-focus crop) — no re-upload, no AI credits.
export const APP_IMAGE_RATIOS = [
  { key: 'landscape', ratio: 1.91, ar: '191-100', label: '1.91:1', name: 'Landscape', minW: 600, minH: 314 },
  { key: 'square', ratio: 1, ar: '1-1', label: '1:1', name: 'Square', minW: 300, minH: 300 },
  { key: 'portrait', ratio: 0.8, ar: '4-5', label: '4:5', name: 'Portrait', minW: 480, minH: 600 },
] as const;
export type AppImageRatio = (typeof APP_IMAGE_RATIOS)[number];

const RATIO_TOLERANCE = 0.02;
const MAX_EDGE = 1200;

export function matchAppRatio(width?: number, height?: number): AppImageRatio | null {
  if (!width || !height) return null;
  const r = width / height;
  return APP_IMAGE_RATIOS.find((s) => Math.abs(r - s.ratio) / s.ratio <= RATIO_TOLERANCE) ?? null;
}

export interface ConformedImage {
  url: string; width?: number; height?: number;
  /** Shape Google will see. */
  shape: AppImageRatio | null;
  /** True when the URL now carries an ImageKit crop. */
  cropped: boolean;
  /** Set when even the best crop is below Google's minimum size. */
  problem?: string;
}

/** Keep conforming images as they are; crop the rest to the nearest allowed shape. */
export function conformAppImage(url: string, width?: number, height?: number): ConformedImage {
  if (!url || !width || !height) return { url, width, height, shape: null, cropped: false };
  const exact = matchAppRatio(width, height);
  if (exact) {
    const problem = width < exact.minW || height < exact.minH ? `${width}×${height} is below Google's minimum ${exact.minW}×${exact.minH} for ${exact.name.toLowerCase()}` : undefined;
    return { url, width, height, shape: exact, cropped: false, problem };
  }
  const r = width / height;
  const target = [...APP_IMAGE_RATIOS].sort((a, b) => Math.abs(Math.log(r / a.ratio)) - Math.abs(Math.log(r / b.ratio)))[0];
  // Largest crop of the original that has the target shape, capped at 1200px wide.
  let w = r > target.ratio ? Math.round(height * target.ratio) : width;
  if (w > MAX_EDGE) w = MAX_EDGE;
  const h = Math.round(w / target.ratio);
  const problem = w < target.minW || h < target.minH ? `after cropping to ${target.label} it would be ${w}×${h}, below Google's minimum ${target.minW}×${target.minH}` : undefined;
  const cropUrl = imagekitTransformUrl(url, `ar-${target.ar},w-${w},fo-auto`);
  if (!cropUrl) return { url, width, height, shape: null, cropped: false, problem: `${width}×${height} is not 1.91:1, 1:1 or 4:5 and this image isn't hosted where it can be cropped — upload it instead` };
  return { url: cropUrl, width: w, height: h, shape: target, cropped: true, problem };
}

/**
 * ImageKit URL → same file with a transform. Must be the PATH form
 * (`/<id>/tr:…/<file>`): on this account the `?tr=` query form is ignored and
 * returns the original bytes. Non-ImageKit URLs return null.
 */
export function imagekitTransformUrl(url: string, transform: string): string | null {
  try {
    const u = new URL(url);
    if (!/(^|\.)imagekit\.io$/.test(u.hostname)) return null;
    const [, id, ...rest] = u.pathname.split('/');
    if (!id || rest.length === 0) return null;
    const cleaned = rest.filter((seg) => !seg.startsWith('tr:'));
    return `${u.origin}/${id}/tr:${transform}/${cleaned.join('/')}`;
  } catch {
    return null;
  }
}
